export interface OrderCorrectionFieldChange {
  field: string;
  before: unknown;
  after: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function comparable(value: unknown): string {
  if (value === undefined) return 'undefined';
  return JSON.stringify(value);
}

/** Top-level audit snapshot diff used by the order-detail correction log. */
export function orderCorrectionFieldChanges(
  before: unknown,
  after: unknown,
): OrderCorrectionFieldChange[] {
  const beforeRecord = isRecord(before) ? before : {};
  const afterRecord = isRecord(after) ? after : {};
  const fields = [...new Set([...Object.keys(beforeRecord), ...Object.keys(afterRecord)])].sort();
  return fields
    .filter((field) => comparable(beforeRecord[field]) !== comparable(afterRecord[field]))
    .map((field) => ({
      field,
      before: beforeRecord[field],
      after: afterRecord[field],
    }));
}

function lineSummary(value: unknown, locale: string): string | null {
  if (!isRecord(value)) return null;
  const code = typeof value.productCode === 'string' ? value.productCode : value.productSkuId;
  if (typeof code !== 'string') return null;
  const name = typeof value.productName === 'string' ? ` — ${value.productName}` : '';
  const qty = typeof value.qty === 'number' ? ` × ${value.qty.toLocaleString(locale)}` : '';
  const comment = typeof value.comment === 'string' && value.comment ? ` (${value.comment})` : '';
  return `${code}${name}${qty}${comment}`;
}

function deliveryGroupSummary(value: unknown, locale: string): string | null {
  if (!isRecord(value) || typeof value.address !== 'string') return null;
  const details: string[] = [value.address];
  if (typeof value.addressComment === 'string' && value.addressComment) {
    details.push(value.addressComment);
  }
  if (Array.isArray(value.phones)) {
    details.push(...value.phones.filter((phone): phone is string => typeof phone === 'string'));
  } else if (typeof value.phone === 'string' && value.phone) {
    details.push(value.phone);
  }
  if (typeof value.deliveryFeeCents === 'number') {
    details.push(
      new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(
        value.deliveryFeeCents / 100,
      ),
    );
  }
  return details.join(' · ');
}

/** Human-readable audit value without leaking raw `[object Object]` strings. */
export function formatOrderCorrectionValue(field: string, value: unknown, locale: string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (field.endsWith('Cents') && typeof value === 'number') {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(
      value / 100,
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    const summaries =
      field === 'lines'
        ? value.map((entry) => lineSummary(entry, locale))
        : field === 'deliveryGroups'
          ? value.map((entry) => deliveryGroupSummary(entry, locale))
          : value.map((entry) => (typeof entry === 'string' ? entry : JSON.stringify(entry)));
    return summaries.filter((entry): entry is string => Boolean(entry)).join('; ') || '—';
  }
  if (typeof value === 'boolean') return value ? '✓' : '✕';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toLocaleString(locale);
  if (typeof value === 'object') return JSON.stringify(value);
  return '—';
}
